import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Styles from './expandable-content.module.less';

const ALLOWED_TAGS = [
    'b',
    'strong',
    'u',
    'i',
    'em',
    'ul',
    'li',
    'ol',
    'br',
    'div',
    'p',
    's',
    'q',
    'font',
    'mark',
];

/* eslint-disable @typescript-eslint/naming-convention */
const purifyConfig = {
    ALLOWED_TAGS,
    FORBID_ATTR: ['style'],
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
};
/* eslint-enable @typescript-eslint/naming-convention */

export interface ExpandableContentProps {
    content: string;
    className?: string;
    moreText?: string;
    moreBlock?: boolean;
    lessText?: string;
    html?: boolean;
    rows: number;
    size?: 'small' | 'medium';
}

export const ExpandableContent: FC<ExpandableContentProps> = props => {
    const {
        className,
        content,
        html,
        lessText,
        moreBlock,
        moreText,
        rows,
        size = 'medium',
    } = props;
    const [isCollapsed, setCollapsed] = useState(true);
    const [isExpandable, setIsExpandable] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkExpandable = () => {
            if (ref.current) {
                setIsExpandable(ref.current.scrollHeight > ref.current.offsetHeight);
            }
        };

        checkExpandable();
        window.addEventListener('resize', checkExpandable);

        return () => window.removeEventListener('resize', checkExpandable);
    }, [ref, content]);

    const containerStyles = useMemo(() => {
        const styles: CSSProperties = {};

        const fontSize = size === 'small' ? 14 : 16;
        const lineHeight = size === 'small' ? 21 : 24;

        styles.fontSize = `${fontSize}px`;
        styles.lineHeight = `${lineHeight}px`;
        styles.fontWeight = 400;

        if (isCollapsed) {
            if (isExpandable) {
                styles.height = `${rows * lineHeight}px`;
            } else {
                styles.maxHeight = `${rows * lineHeight}px`;
            }
        }

        return styles;
    }, [isCollapsed, isExpandable, rows, size]);

    const onExpand = useCallback(() => setCollapsed(false), []);
    const onCollapse = useCallback(() => setCollapsed(true), []);

    const cont = useMemo(() => {
        if (!html) {
            return <div>{content}</div>;
        }

        /* eslint-disable react/no-danger, @typescript-eslint/naming-convention */
        return (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, purifyConfig) }} />
        );
        /* eslint-enable react/no-danger, @typescript-eslint/naming-convention */
    }, [html, content]);

    return (
        <div className={classNames(Styles.container, className)}>
            <div
                style={containerStyles}
                ref={ref}
                className={classNames(Styles.collapsible, { [Styles.expanded]: !isCollapsed })}
            >
                {cont}
            </div>

            {isExpandable ? (
                isCollapsed ? (
                    moreBlock ? (
                        <div className={Styles.moreBlock} onClick={onExpand}>
                            {moreText ?? 'Show more'}
                        </div>
                    ) : (
                        <div className={Styles.moreInline} onClick={onExpand}>
                            ... {moreText ?? 'show more'}
                        </div>
                    )
                ) : moreBlock ? (
                    <div className={Styles.moreBlock} onClick={onCollapse}>
                        {lessText ?? 'Show less'}
                    </div>
                ) : null
            ) : null}
        </div>
    );
};
